import { useTranslation } from 'react-i18next';
import { Spinner } from '@/components/ui';
import { cn } from '@/lib/utils/cn';
import { formatTime } from '@/lib/utils/date';
import type { UseBooking } from '../booking/useBooking';

const WEEKDAYS = ['Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ', 'Du'];
const dateKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
/** Monday-first weekday index (0 = Monday). */
const mondayIndex = (d: Date) => (d.getDay() + 6) % 7;

interface DateTimeSelectorProps {
  booking: UseBooking;
}

/** Month calendar + time-slot picker for manual appointment selection. */
export function DateTimeSelector({ booking }: DateTimeSelectorProps) {
  const { t } = useTranslation();
  const { manual } = booking;

  if (!manual.ready) {
    return (
      <p className="rounded-input bg-muted-100 px-3 py-3 text-sm text-ink-mute">{t('booking.manual_need_doctor')}</p>
    );
  }

  const { month } = manual;
  const monthLabel = month.toLocaleDateString('ro-RO', { month: 'long', year: 'numeric' });
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const leadingBlanks = mondayIndex(new Date(month.getFullYear(), month.getMonth(), 1));

  const availableKeys = new Set(manual.availableDates.map(dateKey));
  const selectedKey = manual.selectedDate ? dateKey(manual.selectedDate) : null;

  return (
    <div className="flex flex-col gap-4">
      {/* Month navigation */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={manual.goPrevMonth}
          disabled={!manual.canPrevMonth}
          className="rounded-input p-2 text-ink-soft hover:bg-muted-100 disabled:opacity-30"
          aria-label={t('common.back')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="m15 18-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <span className="text-sm font-bold capitalize text-ink">{monthLabel}</span>
        <button
          type="button"
          onClick={manual.goNextMonth}
          disabled={!manual.canNextMonth}
          className="rounded-input p-2 text-ink-soft hover:bg-muted-100 disabled:opacity-30"
          aria-label={t('booking.next_month')}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="m9 18 6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      {manual.loadingTimetable ? (
        <div className="flex items-center justify-center gap-2 py-8 text-sm text-ink-mute">
          <Spinner size={18} /> {t('booking.loading_slots')}
        </div>
      ) : (
        <>
          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1 text-center">
            {WEEKDAYS.map((w) => (
              <span key={w} className="py-1 text-xs font-semibold text-ink-mute">
                {w}
              </span>
            ))}
            {Array.from({ length: leadingBlanks }).map((_, i) => (
              <span key={`blank-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const date = new Date(month.getFullYear(), month.getMonth(), i + 1);
              const key = dateKey(date);
              const isAvailable = availableKeys.has(key);
              const isSelected = key === selectedKey;
              return (
                <button
                  key={key}
                  type="button"
                  disabled={!isAvailable}
                  onClick={() => manual.selectDate(date)}
                  className={cn(
                    'flex h-10 items-center justify-center rounded-input text-sm transition-colors',
                    isSelected && 'bg-primary font-bold text-white',
                    !isSelected && isAvailable && 'bg-primary-light font-semibold text-primary hover:bg-primary/20',
                    !isAvailable && 'text-ink-mute/40',
                  )}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>

          {manual.availableDates.length === 0 && (
            <p className="rounded-input bg-muted-100 px-3 py-3 text-center text-sm text-ink-mute">
              {t('booking.no_slots_month')}
            </p>
          )}

          {/* Time slots for the selected day */}
          {manual.selectedDate && (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-semibold text-ink-soft">{t('booking.pick_time')}</span>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {manual.dateIntervals.map((interval) => {
                  const isSel =
                    manual.selectedInterval?.start.getTime() === interval.start.getTime() &&
                    manual.selectedInterval?.entityId === interval.entityId;
                  return (
                    <button
                      key={`${interval.entityId}-${interval.start.getTime()}`}
                      type="button"
                      onClick={() => manual.selectInterval(interval)}
                      className={cn(
                        'rounded-input border px-2 py-2 text-sm font-semibold transition-colors',
                        isSel
                          ? 'border-primary bg-primary text-white'
                          : 'border-muted-300 bg-white text-ink hover:border-primary hover:text-primary',
                      )}
                    >
                      {formatTime(interval.start)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
